import REST_CLIENT from '../utils/restClient';
import IAM_USERS_CONTROLLER from './iamUsers';
import { reconcileUserDsGrants } from '../utils/dsGrantReconcile';
import { toastActions } from '../components/inAppMessages/toastActions';
import LANGUAGES_CONTROLLER from './languages';

export const MODULE_NAME = 'COMPANY_PERMISSIONS_CONTROLLER';

export const COMPANY_PERMISSIONS_TOAST_SCOPE = 'companyPermissions';

function namespaced(name) {
    return `${MODULE_NAME}.${name}`;
}

const LOAD = namespaced('LOAD');
const LOAD_OK = namespaced('LOAD_OK');
const LOAD_FAIL = namespaced('LOAD_FAIL');
const LOAD_COMPANY_DETAILS_OK = namespaced('LOAD_COMPANY_DETAILS_OK');
const LOAD_COMPANY_DETAILS_FAIL = namespaced('LOAD_COMPANY_DETAILS_FAIL');
const EDIT = namespaced('EDIT');
const EDIT_OK = namespaced('EDIT_OK');
const EDIT_FAIL = namespaced('EDIT_FAIL');

export const ACTION_TYPES = {
    LOAD,
    LOAD_OK,
    LOAD_FAIL,
    LOAD_COMPANY_DETAILS_OK,
    LOAD_COMPANY_DETAILS_FAIL,
    EDIT,
    EDIT_OK,
    EDIT_FAIL,
};

export const companyPermissionsReducer = (state = {
    data: undefined,
    loading: false,
    error: null,
}, action) => {
    switch (action.type) {
        case LOAD:
            return Object.assign({}, state, { loading: true, error: null });
        case LOAD_OK:
            return Object.assign({}, state, { data: action.response, loading: false, error: null });
        case LOAD_FAIL:
            return Object.assign({}, state, { data: undefined, loading: false, error: action.error });
        case LOAD_COMPANY_DETAILS_OK:
            const allCompanies = action.response;
            const companiesLookup = allCompanies ?
                Object.fromEntries(allCompanies.map((company) =>
                    [company.id, `${company.code} - ${company.name}`]
                )) :
                {};
            return Object.assign({}, state, { allCompanies: allCompanies, companiesLookup: companiesLookup });
        case LOAD_COMPANY_DETAILS_FAIL:
            return Object.assign({}, state, { allCompanies: undefined });
        case EDIT:
            return Object.assign({}, state, { loading: true, error: null });
        case EDIT_OK:
            return Object.assign({}, state, { loading: false, error: null });
        case EDIT_FAIL:
            return Object.assign({}, state, { loading: false, error: null });
        default:
            return state;
    }
};

export const API = {

    doLoad: (tenantId, iamTenantId, errorHandler) => {
        return async (dispatch) => {
            dispatch({ type: LOAD });
            try {
                const users = await dispatch(IAM_USERS_CONTROLLER.doEnsureLoaded(iamTenantId));

                const companyGrants = await REST_CLIENT.getCompanyPermissions(tenantId);

                if (!companyGrants || !companyGrants.userCompanyIds) {
                    throw new Error('Invalid company grants response');
                }

                const response = users.map(user => ({
                    id: user.userName,
                    iamUserId: user.id,
                    firstName: user.name.givenName,
                    lastName: user.name.familyName,
                    role: user.userAccess?.[0]?.role?.displayName || '-',
                    companyGrants: companyGrants.userCompanyIds?.[user.userName] || []
                }));

                dispatch({ type: LOAD_OK, response: response });
            } catch (error) {
                dispatch({ type: LOAD_FAIL, error });
                errorHandler()
            }
        }
    },

    doEdit: (tenantId, userId, updatedCompanies, successHandler, errorHandler, reconcileContext) => {
        return async (dispatch, getState) => {
            dispatch({ type: EDIT });
            try {
                const response = await REST_CLIENT.updateCompanyPermissionsForUser(tenantId, userId, updatedCompanies);
                if (reconcileContext?.iamTenantId && reconcileContext?.iamUserId) {
                    const reconciled = await reconcileUserDsGrants({
                        iamTenantId: reconcileContext.iamTenantId,
                        iamUserId: reconcileContext.iamUserId,
                        companyGrants: reconcileContext.companyGrants,
                    });
                    const languages = getState().languages;
                    if (reconciled?.changed) {
                        dispatch(toastActions.addToast(
                            'company-permissions-ds-scope-adjusted',
                            LANGUAGES_CONTROLLER.format(languages, 'CompanyPermissions_DSScopeAdjustedTitle'),
                            LANGUAGES_CONTROLLER.format(languages, 'CompanyPermissions_DSScopeAdjusted'),
                            'info',
                            COMPANY_PERMISSIONS_TOAST_SCOPE
                        ));
                    } else if (reconciled === null) {
                        dispatch(toastActions.addToast(
                            'company-permissions-ds-scope-sync-failed',
                            LANGUAGES_CONTROLLER.format(languages, 'CompanyPermissions_DSScopeSyncFailedTitle'),
                            LANGUAGES_CONTROLLER.format(languages, 'CompanyPermissions_DSScopeSyncFailed'),
                            'error',
                            COMPANY_PERMISSIONS_TOAST_SCOPE
                        ));
                    }
                }
                dispatch({ type: EDIT_OK });
                successHandler(response);
            } catch (error) {
                dispatch({ type: EDIT_FAIL });
                errorHandler();
            }
        };
    },

    doLoadCompanies: () => {
        return async (dispatch) => {
            try {
                const response = await REST_CLIENT.getCompaniesSummaryV1();
                dispatch({ type: LOAD_COMPANY_DETAILS_OK, response: response });
            } catch (error) {
                dispatch({ type: LOAD_COMPANY_DETAILS_FAIL });
            }
        }
    },
};

export default API;
